import { ColumnType } from 'antd/es/table';
import { useDispatch } from "react-redux";
import React, { useEffect, useState, useMemo } from "react";
import { updateMenuData } from "../../store/slices/misc/menuData";
import Template1 from "./ReportTemplates/Template1";
import { useLocation, useNavigate } from "react-router-dom";
import { phaseApis } from "../../api/Phase";
import reportApis from "../../api/reports";
import { TooltipComponent } from "../../components/Component";
import { Drawer, Table, Progress, Tooltip } from 'antd';
import { closeAllModals } from "../../store/slices/projectManagement/GlobalStoreUpdate";

interface TaskItem {
  taskId: string;
  taskName: string;
  taskOwnerName: string;
  taskPlannedStartDate: string;
  taskPlannedEndDate: string;
  priorityName: string;
  completionPercentage: number;
  taskStatus: string;
  uid: string;
}

const TagWiseTaskPerformanceReport = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [tasksData, setTasksData] = useState<TaskItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState<{ userId: string, userName: string } | null>(null);
  const [tagFilterData, setTagFilterData] = useState([]);

  const { id: projectId, projectName } = location.state || {};

  const columns: ColumnType<any>[] = [

    {
      title: "Tag Name",
      dataIndex: "tagName",
      key: "tagName",
      width: 50,
      fixed: "left",
      render: (text) => text || "N/A",
    },
    {
      title: "Total Tasks",
      dataIndex: "totalTasks",
      key: "totalTasks",
      width: 50,
      render(text: any, record: TaskItem) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              id={`task-${record?.taskId}`}
              onClick={() => handleTotalTasksClick(record)}
            >
              {text}
            </span>
            <TooltipComponent
              iconClass="info-circle text-info"
              icon={""}
              id={`phase-${record?.taskId}`}
              direction="right"
              text={"Click To View  " + text + " Details"}
              containerClassName={"mt-1"}
            />
          </>
        );
      },
    },
    {
      title: "Delayed Tasks",
      dataIndex: "delayedTasks",
      key: "delayedTasks",
      width: 100,
      render: (text) => (text === null || text === undefined || text === "" ? "N/A" : text),
    },
    {
      title: "Avg.Completion Days",
      dataIndex: "avgCompletionDays",
      key: "avgCompletionDays",
      width: 100,
      render: (text) => (text === null || text === undefined || text === "" ? "N/A" : `${text} Days`)
    },
    {
      title: "Owner With Most Tasks",
      dataIndex: "assignedTo",
      key: "assignedTo",
      width: 150,
      render: (text) => (text === null || text === undefined || text === "" ? "N/A" : text),
    },
    {
      title: "Completed Percentage",
      dataIndex: "completedPercentage",
      key: "completedPercentage",
      width: 150,
      render: (text, record) => {
        if (text === null || text === undefined || text === "") return "N/A";

        const percentage = parseFloat(text.toString().replace('%', ''));

        return (
          <Tooltip
            title={`Completion: ${text}`}
            placement="top"
          >
            <Progress
              strokeLinecap="butt"
              size={[186, 10]}
              percent={percentage}
              status="active"
              strokeColor={{
                from: "#87d068",
                to: "#87d068",
              }}
            />
          </Tooltip>
        );
      }
    },
  ]

  const handleTotalTasksClick = async (record: any) => {
    setSelectedUser({
      userId: record.userId,
      userName: record.assigedTo
    });
    setLoading(true);
    setDrawerVisible(true);

    try {
      const statusForApi = record.taskStatus === "Active" ? "Open" : record.taskStatus;
      const response = await reportApis.fetchPMSReports({
        page: 1,
        pageSize: 10,
        projectIds: projectId,
        pageDiff: 0,
        sortOrder: "DESC",
        direction: "P",
        taskStatus: statusForApi,
        reportName: "tagListForTagWiseTaskPerformance",
        taskId: record.taskId,
        tagIds: record.tagId,
        taskOwnerIds: record.assignedId,
      });
      const { status, data } = response || {};
      if (status) {
        const { count, list, projectResponse } = data || {}
        setTasksData(list || []);
      }

    } catch (error) {
      console.error("Error fetching tasks:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTagFilterData = async () => {
    try {
      const response = await phaseApis.getTagsFilterData(projectId?.toString());
      const { data, status } = response || {};
      if (status) {
        const options = data.map((item: any) => {
          return {
            label: item.tagName,
            value: item.tagId,
          };
        });
        setTagFilterData(options);
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  };


  useEffect(() => {
    fetchTagFilterData();
    dispatch(
      updateMenuData([
        {
          text: "Tag-Wise Task Performance Report",
          link: "pms/tagwise-task-performance-report",

        },
      ])
    );
  }, []);

  const drawerColumns: ColumnType<TaskItem>[] = useMemo(() => [
    {
      title: "Tag Name",
      dataIndex: "tagName",
      key: "tagName",
      width: 100,
      fixed: "left",
      render: (text) => (text === null || text === undefined || text === "" ? "N/A" : text),
    },
    {
      title: "Task Name",
      dataIndex: "taskName",
      key: "taskName",
      width: 250,
      fixed: "left",
      render(text: any, record: any) {
        return (
          <>
            <span
              className="inlineTableCellLink"
              id={`Task-${record?.taskId}`}
              onClick={() => {
                closeAllModals(dispatch);
                navigate(`/pms/project/task-detail/${record.taskId}`, {
                  state: {
                    projectId,
                    projectName,
                    returnPath: location.pathname,
                    returnState: location.state,
                  },
                });
              }}
            >
              {text}
            </span>
          </>
        );
      },
    },
    {
      title: "Owner Name",
      dataIndex: "userName",
      key: "userName",
      width: 250,
      render: (text) => (text === null || text === undefined || text === "" ? "N/A" : text),
    },
    {
      title: "Completion Percentage",
      dataIndex: "completedPercentage",
      key: "completedPercentage",
      width: 200,
      render: (text) => (text === null || text === undefined || text === "" ? "N/A" : text),
    },
    {
      title: "Task Delay Status",
      dataIndex: "DelayStatus",
      key: "DelayStatus",
      width: 200,
      render: (text) => (text === null || text === undefined || text === "" ? "N/A" : text),
    },
    {
      title: "Avg.Completion Days",
      dataIndex: "avgCompletionDays",
      key: "avgCompletionDays",
      width: 100,
      render: (text) => (text === null || text === undefined || text === "" ? "N/A" : text)
    },
  ], [dispatch]);

  return (
    <>
      <Template1
        moduleKey={window.location.pathname}
        columns={columns}
        pageTitle='Tag-Wise Task Performance Report'
        pageDescription=''
        previousPath={{
          link: "/pms/project/reports",
          state: location.state,
        }}
        filters={{
          customFilters: [
            {
              type: "multiple_selection",
              name: "tag",
              label: "Tag",
              options: tagFilterData,
            },
          ]
        }}
      />
      <Drawer
        title={`Tasks for ${selectedUser?.userName || 'User'}`}
        width={1200}
        onClose={() => setDrawerVisible(false)}
        open={drawerVisible}
        bodyStyle={{ paddingBottom: 80 }}
      >
        <Table
          columns={drawerColumns}
          dataSource={tasksData}
          loading={loading}
          scroll={{ x: 1300 }}
          rowKey="taskId"
        />
      </Drawer>
    </>
  );
}

export default TagWiseTaskPerformanceReport;